import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Joyride, { CallBackProps, EVENTS, Step } from 'react-joyride'
import Button from '../../atoms/Button/Button'
import VerticalGroup from '../../atoms/VerticalGroup/VerticalGroup'
import HorizontalGroup from '../../atoms/HorizontalGroup/HorizontalGroup'
import Clickable from '../../atoms/Clickable/Clickable'
import Text from '../../atoms/Text/Text'
import { useAppSelector } from '../../../store'

type Props = {
  steps: Array<Partial<Step>>
  onCallback?: (data: CallBackProps) => void
  storageKey: string
  mode?: 'floater' | 'beacon'
}

const commonStepProps = {
  hideFooter: true,
}

const CustomNextButton: React.FC<{
  onClick: () => void
  isLastStep: boolean
}> = ({ onClick, isLastStep }) => (
  <Button rounded="full" onClick={onClick}>
    {isLastStep ? 'Close' : 'Next'}
  </Button>
)

const CustomSkipButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Clickable onClick={onClick}>
    <Text>Skip</Text>
  </Clickable>
)

const CustomProgress: React.FC<{ currentStep: number; totalSteps: number }> = ({
  currentStep,
  totalSteps,
}) => (
  <>
    {totalSteps > 1 && (
      <Text>
        {currentStep + 1}/{totalSteps}
      </Text>
    )}
  </>
)

export interface InAppHelpState {
  eventId: string
  currentRound: number
}

const InAppHelp: React.FC<Props> = ({
  steps,
  onCallback,
  storageKey,
  mode = 'floater',
}) => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)

  const { eventId } = useAppSelector(state => ({
    eventId: state.event.details?.eventId,
  }))

  const fullStorageKey = useMemo(
    () => `${eventId}-${storageKey}`,
    [eventId, storageKey],
  )

  useEffect(() => {
    const key = localStorage.getItem(fullStorageKey)
    if (key !== 'true') {
      setRun(true)
    }
  }, [fullStorageKey])

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { type, step } = data
      if (type === EVENTS.STEP_AFTER) {
        const { index } = data
        console.log(`Step ${index + 1} completed (${step.target})`)
        setStepIndex(index + 1)
      }
      if (type === EVENTS.TOUR_END) {
        localStorage.setItem(fullStorageKey, 'true')
      }
      if (onCallback) {
        onCallback(data)
      }
    },
    [onCallback, fullStorageKey],
  )

  const handleNextClick = useCallback(() => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1)
    } else {
      setRun(false)
      localStorage.setItem(fullStorageKey, 'true')
    }
  }, [stepIndex, steps.length, fullStorageKey])

  const handleSkipClick = useCallback(() => {
    setRun(false)
    localStorage.setItem(fullStorageKey, 'true')
  }, [fullStorageKey])

  const stepsWithCustomButton = useMemo(
    () =>
      steps.map(step => ({
        ...commonStepProps,
        ...step,
        target: step.target || '',
        disableBeacon: mode === 'floater',
        placementBeacon: 'top' as const,
        floaterProps:
          mode === 'floater'
            ? {
                offset: 10,
                styles: {
                  floater: { transform: 'translate(-30px, 40px)' },
                },
              }
            : undefined,
        styles: {
          beacon: {
            margin: 17,
          },
        },
        content: (
          <VerticalGroup gap={4}>
            {step.content}
            <HorizontalGroup between fullWidth verticalCenter>
              <HorizontalGroup
                verticalCenter
                between={steps.length > 1}
                right={steps.length === 1}
                fullWidth
              >
                {steps.length > 1 && (
                  <>
                    <CustomProgress
                      currentStep={stepIndex}
                      totalSteps={steps.length}
                    />
                    <CustomSkipButton onClick={handleSkipClick} />
                  </>
                )}
                <CustomNextButton
                  onClick={handleNextClick}
                  isLastStep={stepIndex === steps.length - 1}
                />
              </HorizontalGroup>
            </HorizontalGroup>
          </VerticalGroup>
        ),
      })),
    [steps, handleSkipClick, handleNextClick, stepIndex, mode],
  )

  return (
    <Joyride
      steps={stepsWithCustomButton}
      run={run}
      callback={handleJoyrideCallback}
      continuous={true}
      stepIndex={stepIndex}
      styles={{
        options: {
          primaryColor: '#28A4F2',
        },
      }}
    />
  )
}

export default InAppHelp
